import { useEffect, useRef, useState } from "react";

import type { LanguageCopy, LanguageKey } from "../api/client";

import { LanguageCard } from "./LanguageCard";

type Props = {
  ranked: LanguageKey[];
  languages: Record<LanguageKey, LanguageCopy>;
  youScores: Record<LanguageKey, number>;
};

export function LanguageCardCarousel({ ranked, languages, youScores }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const primary = ranked[0];

  // Track the active slide via scroll position for the visual indicator below.
  // The indicator is presentational only — keyboard nav isn't supported, and
  // the scroll listener only needs re-attaching when the number of slides
  // changes, so we depend on ranked.length (stable for a given assessment).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let rafId = 0;
    const update = () => {
      const scrollerCenter = scroller.scrollLeft + scroller.clientWidth / 2;
      let closest = 0;
      let closestDelta = Infinity;
      slideRefs.current.forEach((el, i) => {
        if (!el) return;
        const slideCenter = el.offsetLeft + el.offsetWidth / 2;
        const delta = Math.abs(slideCenter - scrollerCenter);
        if (delta < closestDelta) {
          closestDelta = delta;
          closest = i;
        }
      });
      setActiveIndex(closest);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [ranked.length]);

  return (
    <div>
      <div
        ref={scrollerRef}
        className="wren-card-carousel flex snap-x snap-mandatory overflow-x-auto -mx-6 px-6 pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {ranked.map((key, i) => (
          <div
            key={key}
            ref={(el) => { slideRefs.current[i] = el; }}
            className="flex shrink-0 snap-center pr-4 last:pr-0"
            style={{ width: "calc(100% - 1.5rem)" }}
          >
            <LanguageCard
              copy={languages[key]}
              score={youScores[key] ?? 0}
              isPrimary={key === primary}
            />
          </div>
        ))}
      </div>

      {/* Visual-only indicator. Not interactive; swipe/scroll is the only control. */}
      <div
        className="mt-6 flex items-center justify-center gap-2"
        aria-hidden="true"
      >
        {ranked.map((key, i) => {
          const active = i === activeIndex;
          return (
            <span
              key={key}
              className="h-2 rounded-full transition-all"
              style={{
                width: active ? 24 : 8,
                background: active
                  ? "var(--color-wren-green)"
                  : "var(--color-wren-border)",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
